import { useMemo, useState, type ReactNode } from 'react';
import type { ServerTask } from '../../data/tasks';
import type { Store } from '../../data/types';
import { isoDaysAgo, isoToCompanyDay } from '../dates';
import { StoreSearchSelect } from '../StoreSearchSelect';
import { TaskRow } from './TaskRow';
import { CsvButton } from '../CsvButton';
import { TASKS_CSV_HEADER, tasksCsvRows } from './tasksCsv';
import { PagerBar, PagerTop, usePager } from '../Pager';
import { groupTasks, type GroupBy } from './taskGroups';

type SortBy = 'due' | 'created' | 'store' | 'assignee';
export type StatusFilter = 'open' | 'completed' | 'all';
/** '' = all time; a number = "since N days ago"; 'custom' = manual range. */
type DatePreset = '' | '7' | '30' | '90' | 'custom';

/**
 * Task list with a filter toolbar: status (open/completed/all — the list is
 * fed both), district + store, a date range with presets, and sort. The date
 * a task is matched on is its completion date once completed, otherwise its
 * due date (the date filter; "Sort: created date" orders by creation); presets are "since N
 * days ago" with no upper bound so open tasks due in the future still show.
 * District/store controls appear only when the list spans more than one.
 * Sort by due (or completed) date, created date (newest first), store or
 * assignee; group by district or store (owner ask 2026-10-29) — each group
 * a collapsed section with its count and overdue count, paged inside.
 * Shared by the Tasks hub and the district pages; the caller owns fetching,
 * scoping, and the complete/reopen flow.
 */
export function TaskListView({
  tasks,
  filterStores,
  storeById,
  canManage,
  onComplete,
  onReopen,
  pageSize,
  initialStoreId = '',
  initialStatus = 'open',
}: {
  /** Open AND completed tasks, already scoped to the viewer; null = loading. */
  tasks: ServerTask[] | null;
  /** Stores offered in the filter dropdown (pre-sorted, active). */
  filterStores: Store[];
  /** Resolution map — may include inactive stores so history renders. */
  storeById: Map<number, Store>;
  canManage: boolean;
  /** Omitted = no Complete button (read-only roles). */
  onComplete?: ((task: ServerTask) => void) | undefined;
  onReopen: (task: ServerTask) => void;
  /** Starts filtered to this store (a store page's "View all" link — owner ask 2026-10-29). */
  initialStoreId?: string;
  initialStatus?: StatusFilter;
  /** Tasks per page (owner ruling 2026-10-29: a page at a time; default 10, 5 on a phone). */
  pageSize?: number;
}) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialStatus);
  const [districtFilter, setDistrictFilter] = useState('');
  const [storeFilter, setStoreFilter] = useState(initialStoreId);
  const [datePreset, setDatePreset] = useState<DatePreset>('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('due');
  const [groupBy, setGroupBy] = useState<GroupBy>('');
  // Completions with no photo/file on the task (exec reflect 2026-10-25:
  // proof-less completions were only caught by DM reopens). Tasks.FileCount.
  const [withoutProof, setWithoutProof] = useState(false);
  const proofFilter = withoutProof && statusFilter !== 'open';

  const multiStore = filterStores.length > 1;

  const districts = useMemo(() => {
    const names = new Map<string, string>();
    for (const s of filterStores) {
      const d = s.district.trim();
      if (d) names.set(d.toLowerCase(), d);
    }
    return [...names.values()].sort((a, b) => a.localeCompare(b));
  }, [filterStores]);
  const multiDistrict = districts.length > 1;

  const storeOptions = useMemo(
    () =>
      districtFilter
        ? filterStores.filter(
            (s) => s.district.trim().toLowerCase() === districtFilter.toLowerCase(),
          )
        : filterStores,
    [filterStores, districtFilter],
  );

  /** Completed tasks are matched on when they closed; open ones on due date. */
  const relevantDay = (t: ServerTask) =>
    t.status === 'complete' ? isoToCompanyDay(t.completedAt) : t.dueDate;

  const effFrom =
    datePreset === 'custom'
      ? fromDate
      : datePreset
        ? isoDaysAgo(Number(datePreset))
        : '';
  const effTo = datePreset === 'custom' ? toDate : '';

  /** Filters that narrow WITHIN a status (drive the "no match" message). */
  const narrowed =
    districtFilter !== '' || storeFilter !== '' || datePreset !== '' || proofFilter;
  const filtersActive = statusFilter !== 'open' || narrowed;

  const visibleTasks = useMemo(() => {
    if (tasks === null) return null;
    const filtered = tasks.filter((t) => {
      if (statusFilter === 'open' && t.status === 'complete') return false;
      if (statusFilter === 'completed' && t.status !== 'complete') return false;
      // Known to have no file only (an older task's count is unknown — QA 2026-10-25).
      if (proofFilter && (t.status !== 'complete' || t.fileCount !== 0)) return false;
      if (storeFilter && String(t.storeId) !== storeFilter) return false;
      if (districtFilter) {
        const d = storeById.get(t.storeId)?.district.trim() ?? '';
        if (d.toLowerCase() !== districtFilter.toLowerCase()) return false;
      }
      const day = relevantDay(t);
      if (effFrom && day < effFrom) return false;
      if (effTo && day > effTo) return false;
      return true;
    });
    const storeKey = (t: ServerTask) =>
      storeById.get(t.storeId)?.storeNumber ?? String(t.storeId);
    // Open: soonest due first. Completed: newest-closed first. All: the open
    // ones first (soonest due), then completions newest first — it used to
    // open on year-old completions (store round 2 F5).
    const dayCmp = (a: ServerTask, b: ServerTask) => {
      if (statusFilter === 'all') {
        const ad = a.status === 'complete';
        const bd = b.status === 'complete';
        if (ad !== bd) return ad ? 1 : -1;
        return ad ? relevantDay(b).localeCompare(relevantDay(a)) : relevantDay(a).localeCompare(relevantDay(b));
      }
      return statusFilter === 'completed'
        ? relevantDay(b).localeCompare(relevantDay(a))
        : relevantDay(a).localeCompare(relevantDay(b));
    };
    return [...filtered].sort((a, b) => {
      if (sortBy === 'store') {
        return (
          storeKey(a).localeCompare(storeKey(b), undefined, { numeric: true }) ||
          dayCmp(a, b)
        );
      }
      if (sortBy === 'assignee') {
        return a.assignedTo.localeCompare(b.assignedTo) || dayCmp(a, b);
      }
      if (sortBy === 'created') {
        // Newest first; a task with no created date (very old rows) last.
        return (b.createdAt ?? '').localeCompare(a.createdAt ?? '') || dayCmp(a, b);
      }
      return dayCmp(a, b);
    });
  }, [tasks, statusFilter, proofFilter, storeFilter, districtFilter, effFrom, effTo, sortBy, storeById]);
  const pager = usePager(visibleTasks, `${statusFilter}|${proofFilter}|${storeFilter}|${districtFilter}|${effFrom}|${effTo}|${sortBy}`, pageSize);
  // Grouping only offers what the list spans (more than one district / store).
  const groupShown: GroupBy = groupBy === 'district' && multiDistrict ? 'district' : groupBy === 'store' && multiStore ? 'store' : '';
  const groups = useMemo(
    () => (groupShown && visibleTasks !== null ? groupTasks(visibleTasks, groupShown, storeById) : null),
    [groupShown, visibleTasks, storeById],
  );

  const clearFilters = () => {
    setStatusFilter('open');
    setDistrictFilter('');
    setStoreFilter('');
    setDatePreset('');
    setFromDate('');
    setToDate('');
    setWithoutProof(false);
  };

  return (
    <>
      {/* Always shown once loaded — even with nothing open, the status
          filter is the only way to reach completed tasks. */}
      {tasks !== null && (
        <div className="list-tools">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            aria-label="Task status"
          >
            <option value="open">Open tasks</option>
            <option value="completed">Completed tasks</option>
            <option value="all">All tasks</option>
          </select>
          {multiDistrict && (
            <select
              value={districtFilter}
              onChange={(e) => {
                const d = e.target.value;
                setDistrictFilter(d);
                // Drop a store pick that falls outside the new district.
                if (d && storeFilter) {
                  const store = filterStores.find((s) => s.id === storeFilter);
                  if (
                    !store ||
                    store.district.trim().toLowerCase() !== d.toLowerCase()
                  ) {
                    setStoreFilter('');
                  }
                }
              }}
              aria-label="District"
            >
              <option value="">All districts</option>
              {districts.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          )}
          {multiStore && (
            <StoreSearchSelect
              stores={storeOptions}
              value={storeFilter}
              onChange={setStoreFilter}
              placeholder="All stores"
            />
          )}
          <select
            value={datePreset}
            onChange={(e) => setDatePreset(e.target.value as DatePreset)}
            aria-label="Date range"
          >
            <option value="">All dates</option>
            <option value="7">Last week</option>
            <option value="30">Last month</option>
            <option value="90">Last quarter</option>
            <option value="custom">Custom range…</option>
          </select>
          {datePreset === 'custom' && (
            <>
              <label className="date-range">
                From
                <input
                  type="date"
                  value={fromDate}
                  max={toDate || undefined}
                  onChange={(e) => setFromDate(e.target.value)}
                  aria-label="Due or completed on or after"
                />
              </label>
              <label className="date-range">
                to
                <input
                  type="date"
                  value={toDate}
                  min={fromDate || undefined}
                  onChange={(e) => setToDate(e.target.value)}
                  aria-label="Due or completed on or before"
                />
              </label>
            </>
          )}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            aria-label="Sort"
          >
            <option value="due">
              {statusFilter === 'open' ? 'Sort: due date' : statusFilter === 'completed' ? 'Sort: completed date' : 'Sort: due / completed date'}
            </option>
            <option value="created">Sort: created date</option>
            <option value="store">Sort: store</option>
            <option value="assignee">Sort: assignee</option>
          </select>
          {(multiDistrict || multiStore) && (
            <select value={groupShown} onChange={(e) => setGroupBy(e.target.value as GroupBy)} aria-label="Group">
              <option value="">No grouping</option>
              {multiDistrict && <option value="district">Group by district</option>}
              {multiStore && <option value="store">Group by store</option>}
            </select>
          )}
          {statusFilter !== 'open' && (
            <label className="checkbox group-toggle">
              <input type="checkbox" checked={withoutProof} onChange={(e) => setWithoutProof(e.target.checked)} />
              Only completed without a photo or PDF
            </label>
          )}
          {filtersActive && (
            <button className="btn btn-small" onClick={clearFilters}>
              Clear filters
            </button>
          )}
          {visibleTasks !== null && visibleTasks.length > 0 && (
            <CsvButton name="tasks" header={TASKS_CSV_HEADER} rows={() => tasksCsvRows(visibleTasks, storeById)} />
          )}
        </div>
      )}

      {visibleTasks === null && <p>Loading…</p>}
      {visibleTasks !== null && visibleTasks.length === 0 && (
        <p className="muted task-list-empty">
          {narrowed && tasks !== null && tasks.length > 0
            ? 'No tasks match these filters.'
            : statusFilter === 'completed'
              ? 'No completed tasks yet.'
              : statusFilter === 'all'
                ? 'No tasks yet.'
                : 'No open tasks. 🎉'}
        </p>
      )}

      {groups !== null ? (
        groups.map((g) => (
          <TaskGroup
            key={`${groupShown}:${g.key}${groups.length === 1 ? ':only' : ''}`}
            label={g.label}
            tasks={g.tasks}
            overdue={g.overdue}
            // One group left (a filter pins it): open, not a lone closed heading.
            startOpen={groups.length === 1}
            resetKey={`${statusFilter}|${proofFilter}|${storeFilter}|${districtFilter}|${effFrom}|${effTo}|${sortBy}`}
            render={(t) => (
              <TaskRow
                key={t.id}
                task={t}
                store={storeById.get(t.storeId)}
                view={t.status === 'complete' ? 'completed' : 'open'}
                canManage={canManage}
                onComplete={onComplete && (() => onComplete(t))}
                onReopen={() => onReopen(t)}
              />
            )}
          />
        ))
      ) : (
        <>
          <PagerTop pager={pager} />
          {pager.items.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              store={storeById.get(t.storeId)}
              view={t.status === 'complete' ? 'completed' : 'open'}
              canManage={canManage}
              onComplete={onComplete && (() => onComplete(t))}
              onReopen={() => onReopen(t)}
            />
          ))}
          <PagerBar pager={pager} noun="tasks" />
        </>
      )}
    </>
  );
}

/**
 * One district's / store's tasks: a collapsed section (owner rule 2026-10-29:
 * no section over ~80% of the screen) whose heading says how many and how
 * many are overdue; opened, it pages a window's worth at a time.
 */
function TaskGroup({
  label,
  tasks,
  overdue,
  startOpen,
  resetKey,
  render,
}: {
  label: string;
  tasks: ServerTask[];
  overdue: number;
  startOpen: boolean;
  resetKey: string;
  render: (task: ServerTask) => ReactNode;
}) {
  const [open, setOpen] = useState(startOpen);
  // Sized to the window (not the hub's 25): an open group stays under ~80% of the screen.
  const pager = usePager(tasks, resetKey);
  return (
    <details className="task-group" open={open}>
      <summary
        className="task-group-head"
        onClick={(e) => {
          e.preventDefault();
          setOpen((o) => !o);
        }}
      >
        {label}
        <span className="muted">
          {' '}
          ({tasks.length}
          {overdue > 0 && <span className="error"> · {overdue} overdue</span>})
        </span>
      </summary>
      {open && (
        <>
          <PagerTop pager={pager} />
          {pager.items.map(render)}
          <PagerBar pager={pager} noun="tasks" />
        </>
      )}
    </details>
  );
}
